import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Region, useRegion } from '../../src/contexts/RegionContext';
import { useTranslation } from 'react-i18next';

/**
 * 🌍 REGION SELECTOR MODAL
 * Two-step confirmation for permanent marketplace selection.
 */
const RegionSelectorModal: React.FC = () => {
    const { i18n, t } = useTranslation('marketplace');
    const { setRegion } = useRegion();
    const [selected, setSelected] = useState<Region | null>(null);
    const [isConfirming, setIsConfirming] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const regions: { id: Region; name: string; flag: string; currency: string; lang: string; desc?: string }[] = [
        {
            id: 'TR',
            name: t('regionSelector.regions.TR.name'),
            flag: '🇹🇷',
            currency: 'TRY (₺)',
            lang: 'tr-TR'
        },
        {
            id: 'US',
            name: t('regionSelector.regions.US.name'),
            flag: '🇺🇸',
            currency: 'USD ($)',
            lang: 'en-US'
        },
        {
            id: 'UK',
            name: t('regionSelector.regions.UK.name'),
            flag: '🇬🇧',
            currency: 'GBP (£)',
            lang: 'en-GB'
        }
    ];

    const handleSelect = async (regionId: Region) => {
        const found = regions.find(r => r.id === regionId);
        if (!found) return;

        setSelected(regionId);
        setIsConfirming(true);

        // Immediately switch language for confirmation step
        await i18n.changeLanguage(found.lang);
    };

    const handleBack = async () => {
        setIsConfirming(false);
        setSelected(null);
        // Switch back to English default
        await i18n.changeLanguage('en-US');
    };

    const handleFinalConfirm = async () => {
        if (!selected) return;
        setIsSubmitting(true);
        try {
            await setRegion(selected);
            localStorage.setItem('fct_region_selected', 'true');
        } catch (error) {
            console.error(error);
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md font-sans">
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-brand-white rounded-[2.5rem] w-full max-w-lg overflow-hidden shadow-2xl border border-white/10"
            >
                <div className="p-8 text-center">
                    <AnimatePresence mode="wait">
                        {!isConfirming ? (
                            <motion.div
                                key="selection"
                                initial={{ x: 20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: -20, opacity: 0 }}
                            >
                                <h2 className="text-3xl font-black text-gray-50 mb-2 italic tracking-tight drop-shadow-md">FASTCASHTRADE</h2>
                                <p className="text-gray-500 font-medium mb-8">
                                    {t('regionSelector.subtitle')}
                                </p>

                                <div className="space-y-3">
                                    {regions.map((r) => (
                                        <button
                                            key={r.id}
                                            onClick={() => handleSelect(r.id)}
                                            className="w-full group p-5 rounded-3xl border-2 border-gray-100 dark:border-white/5 hover:border-brand-accent hover:bg-brand-accent/5 transition-all flex items-center gap-4 text-left bg-white dark:bg-gray-800/50 shadow-sm hover:shadow-md"
                                        >
                                            <span className="text-4xl">{r.flag}</span>
                                            <div>
                                                <div className="font-bold text-gray-900 dark:text-white drop-shadow-sm">{r.name}</div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400 font-bold">
                                                    {t(`regionSelector.regions.${r.id}.desc`)}
                                                </div>
                                            </div>
                                            <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-gray-400">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7" /></svg>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                <p className="mt-8 text-[10px] text-gray-400 font-bold uppercase tracking-widest px-8">
                                    {t('regionSelector.warning')}
                                </p>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="confirm"
                                initial={{ x: 20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: -20, opacity: 0 }}
                                className="py-4"
                            >
                                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl">
                                    ⚠️
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-4">
                                    {t('regionSelector.confirmTitle')}
                                </h3>
                                <div className="bg-gray-50 dark:bg-gray-900/50 rounded-3xl p-6 mb-6 text-left border border-gray-100 dark:border-white/5">
                                    <div className="flex items-center gap-3 mb-4">
                                        <span className="text-3xl">{regions.find(r => r.id === selected)?.flag}</span>
                                        <span className="font-bold text-xl text-gray-900 dark:text-white">{regions.find(r => r.id === selected)?.name}</span>
                                    </div>
                                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 font-medium">
                                        <li className="flex items-center gap-2">
                                            ✅ {t('regionSelector.currency')}: <span className="font-bold text-black dark:text-white">{regions.find(r => r.id === selected)?.currency}</span>
                                        </li>
                                        <li className="flex items-center gap-2">
                                            ✅ {t('regionSelector.language')}: <span className="font-bold text-black dark:text-white">{selected === 'TR' ? 'Türkçe' : 'English'}</span>
                                        </li>
                                    </ul>
                                </div>

                                <div className="p-4 bg-red-50 rounded-2xl border border-red-100 mb-8">
                                    <p className="text-xs text-red-600 font-bold leading-relaxed">
                                        {t('regionSelector.permanentWarning')}
                                    </p>
                                </div>

                                <div className="flex flex-col gap-3">
                                    <button
                                        onClick={handleFinalConfirm}
                                        disabled={isSubmitting}
                                        className="w-full py-5 bg-black text-white rounded-3xl font-black text-lg hover:bg-gray-900 transition-colors disabled:opacity-50"
                                    >
                                        {isSubmitting ? t('regionSelector.processing') : t('regionSelector.confirmButton')}
                                    </button>
                                    <button
                                        onClick={handleBack}
                                        disabled={isSubmitting}
                                        className="w-full py-4 text-gray-400 font-bold hover:text-gray-600 transition-colors"
                                    >
                                        {t('regionSelector.backButton')}
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </div>
    );
};

export default RegionSelectorModal;
